import React from "react";
import Grid from "@material-ui/core/Grid";
import { getIn, useFormikContext } from "formik";
import { Warning } from "components/ErrorMessage";
import { withNamespace } from "components/Form/FormikControls";
import { CenteredGreedy, Row } from "components/Layout";
import { OwnEmailRecipientsTable } from "components/Business/BusinessForm/EmailRecipients/OwnEmailRecipientsTable";
import { OtherEmailRecipientsModal } from "components/Business/BusinessForm/EmailRecipients/OtherEmailRecipientsModal";
import { SlimButton } from "components/Button";
import { EmailRecipientFormSection } from "./EmailRecipientForm";

import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface PrimaryContactWarningProps {
  email: string | null;
  fallbackEmail: string | null;
}

const PrimaryContactWarning: React.FC<PrimaryContactWarningProps> = ({
  email,
  fallbackEmail,
}) => {
  const fallbackEmailWarning = fallbackEmail
    ? `${fallbackEmail} will be used as the primary contact for now.`
    : "";
  const emailWarning = email
    ? `${email} is not a known contact.`
    : `No primary contact set. ${fallbackEmailWarning}`;
  return (
    <CenteredGreedy>
      <Warning>
        {emailWarning} Please set one of the contacts below as the primary
        contact!
      </Warning>
    </CenteredGreedy>
  );
};

interface EmailRecipientsTableProps {
  businessId: string;
  primaryContactEmail: string | null;
  namespace: string;
  onSetPrimaryContactEmail: (email: string) => void;
}

export function EmailRecipientsTable(props: EmailRecipientsTableProps) {
  const {
    businessId,
    primaryContactEmail,
    namespace: ns,
    onSetPrimaryContactEmail,
  } = props;
  const formikContext = useFormikContext();
  const { values, setFieldValue } = formikContext;

  const emailRecipients = getIn(values, ns) as EmailRecipient[] | undefined;

  const hasPrimaryContact =
    primaryContactEmail &&
    emailRecipients?.some(recipient => recipient.email === primaryContactEmail);

  function onClickAddEmailRecipient(): void {
    setFieldValue(
      withNamespace(ns, "editingValue"),
      {
        isCommercialReportRecipient: true,
        isComplianceReportRecipient: true,
      },
      true,
    );
  }

  const hasEmailRecipients = emailRecipients?.length > 0;

  const contactsWithEmails = emailRecipients?.filter(er => er.email);

  const firstEmail =
    contactsWithEmails && contactsWithEmails.length > 0
      ? contactsWithEmails[0].email
      : null;

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={7} md={8}>
          {hasEmailRecipients && !hasPrimaryContact ? (
            <PrimaryContactWarning
              email={primaryContactEmail}
              fallbackEmail={firstEmail}
            />
          ) : null}
        </Grid>
        <Grid item xs={12} sm={5} md={4} container justifyContent="flex-end">
          <Row justifyBetween alignCenter>
            <OtherEmailRecipientsModal namespace={ns} businessId={businessId} />
            &nbsp;
            <SlimButton
              onClick={onClickAddEmailRecipient}
              type="button"
              data-tour="addContact"
            >
              <FontAwesomeIcon icon={faPlus} />
              &nbsp;Contact
            </SlimButton>
          </Row>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <OwnEmailRecipientsTable
            businessId={businessId}
            primaryContactEmail={primaryContactEmail}
            namespace={ns}
            onSetPrimaryContactEmail={onSetPrimaryContactEmail}
          />
        </Grid>
      </Grid>

      <EmailRecipientFormSection
        namespace={ns}
        onSetPrimaryContactEmail={onSetPrimaryContactEmail}
      />
    </>
  );
}
